import React, { useState, useMemo, useEffect, useRef } from "react";
import debounce from "lodash/debounce";
import api from "@/lib/api";
import { maskPhone } from "@/lib/phone";
import { PhoneLockButton, usePhoneLock } from "@/components/crm/MaskedPhoneInput";
import { Input } from "@/components/ui/input";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { Search, Loader2 } from "lucide-react";

// `lockedValue`: when the field still holds a number pre-filled from a selected
// lead/patient, it's shown masked + read-only with a "Change" button (see
// MaskedPhoneInput). Typed values show as typed.
export function LeadSearchInput({ value, onChange, onLeadSelect, placeholder, searchEndpoint, searchParam = "search", lockedValue, "data-testid": dataTestId }) {
  const [locked, unlock] = usePhoneLock(value, lockedValue);
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const controllerRef = useRef(null);

  const searchLeads = useMemo(
    () =>
      debounce(async (query) => {
        controllerRef.current?.abort();
        if (query.length < 2) {
          setResults([]);
          setOpen(false);
          return;
        }
        const controller = new AbortController();
        controllerRef.current = controller;
        setLoading(true);
        try {
          const { data } = await api.get(searchEndpoint || "/leads", {
            params: { [searchParam]: query, limit: 8 },
            signal: controller.signal,
          });
          const items = data?.items || data || [];
          setResults(items);
          setOpen(items.length > 0);
        } catch (e) {
          if (e.name !== "CanceledError" && e.name !== "AbortError") setResults([]);
        } finally {
          setLoading(false);
        }
      }, 300),
    [searchEndpoint, searchParam]
  );

  useEffect(() => () => searchLeads.cancel(), [searchLeads]);

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    searchLeads(v);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div className="relative">
          {locked ? (
            <>
              <Input value={maskPhone(value)} readOnly className="pr-16" placeholder={placeholder} data-testid={dataTestId} />
              <PhoneLockButton onClick={() => { unlock(); onChange(""); }} />
            </>
          ) : (
            <Input
              value={value}
              onChange={handleChange}
              placeholder={placeholder}
              data-testid={dataTestId}
            />
          )}
          {loading && (
            <Loader2 className="absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-muted-foreground" />
          )}
        </div>
      </PopoverAnchor>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] min-w-[280px] p-0"
        align="start"
        sideOffset={4}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="max-h-[200px] overflow-y-auto py-1">
          {results.map((lead) => (
            <button
              key={lead.id}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
              onMouseDown={(e) => {
                e.preventDefault();
                onLeadSelect(lead);
                setOpen(false);
              }}
              data-testid={`lead-suggestion-${lead.id}`}
            >
              <Search className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <span className="font-medium text-ink">{lead.name}</span>
                {lead.mobile && (
                  <span className="ml-1.5 text-xs text-muted-foreground">· {maskPhone(lead.mobile)}</span>
                )}
                {lead.subtitle && (
                  <span className={`ml-1.5 rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                    lead.kind === "patient" ? "bg-navy/10 text-navy" : "bg-nude text-muted-foreground"}`}>
                    {lead.subtitle}
                  </span>
                )}
              </div>
              {lead.interested_service && (
                <span className="flex-shrink-0 text-[11px] text-muted-foreground">
                  {lead.interested_service}
                </span>
              )}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
